import { type IRequestForm, type TRequestStepField } from './@interfaces';

/**
 * ECA Digital (RAD-44): CPF test on the order form.
 *
 * The CPF field is injected on the client, only for the categories under test
 * and only while the `order-cpf-test` feature flag is on. The backend accepts
 * the field as optional, so nothing here is required for the order to be created.
 */
export const ORDER_CPF_TEST_SLUGS = [
  'reformas-e-reparos/pedreiro/portas-e-janelas',
  'reformas-e-reparos/marceneiro/reparo-e-acabamento',
];

export const CPF_FIELD_NAME = 'cpf';

const TERMS_FIELD_NAME = 'terms';

/**
 * `react-json-form` only formats the `phone` type and only validates
 * `regexPattern` for `phone`/`zipcode`: a `text` input gets `required` and
 * `minLength` alone. So the mask is applied by `useCpfMask` on the page and
 * `minLength` is the masked length, which guarantees the `000.000.000-00`
 * format. The backend strips the punctuation and checks the verification
 * digits without ever blocking the order.
 */
export const CPF_MASK = '000.000.000-00';
export const CPF_MASKED_LENGTH = CPF_MASK.length;

export const CPF_FIELD: TRequestStepField = {
  title: 'CPF',
  id: CPF_FIELD_NAME,
  name: CPF_FIELD_NAME,
  type: 'text',
  placeholder: CPF_MASK,
  required: true,
  value: null,
  wrapperClassName: 'form__field input',
  minLength: CPF_MASKED_LENGTH,
  maxLength: CPF_MASKED_LENGTH,
};

/**
 * Formats progressively while typing: `449` -> `449`, `4492` -> `449.2`,
 * `44920069820` -> `449.200.698-20`. Anything that is not a digit is dropped.
 */
export const formatCpf = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);

  let formatted = digits.slice(0, 3);
  if (digits.length > 3) {
    formatted += `.${digits.slice(3, 6)}`;
  }
  if (digits.length > 6) {
    formatted += `.${digits.slice(6, 9)}`;
  }
  if (digits.length > 9) {
    formatted += `-${digits.slice(9, 11)}`;
  }

  return formatted;
};

export const isOrderCpfTestCategory = (slug?: string | null): boolean =>
  !!slug && ORDER_CPF_TEST_SLUGS.includes(slug);

interface IShouldAskCpfParams {
  flagEnabled: boolean;
  slug?: string | null;
  hasCpf: boolean;
}

export const shouldAskCpf = ({
  flagEnabled,
  slug,
  hasCpf,
}: IShouldAskCpfParams): boolean =>
  flagEnabled && isOrderCpfTestCategory(slug) && !hasCpf;

const hasCpfField = (fields: TRequestStepField[]) =>
  fields.some((field) => field.name === CPF_FIELD_NAME);

/**
 * Returns a copy of the form with the CPF field in the last step, right before
 * the terms checkbox. Keeps the number of steps untouched because
 * `react-json-form` assumes a fixed number of personal-data steps at the end.
 */
export const addCpfField = (form: IRequestForm): IRequestForm => {
  if (!form?.steps?.length) {
    return form;
  }

  const lastIndex = form.steps.length - 1;
  const lastStep = form.steps[lastIndex];

  if (hasCpfField(lastStep.fields)) {
    return form;
  }

  const termsIndex = lastStep.fields.findIndex(
    (field) => field.name === TERMS_FIELD_NAME
  );
  const insertAt = termsIndex === -1 ? lastStep.fields.length : termsIndex;

  const fields = [
    ...lastStep.fields.slice(0, insertAt),
    { ...CPF_FIELD },
    ...lastStep.fields.slice(insertAt),
  ];

  const steps = [...form.steps];
  steps[lastIndex] = { ...lastStep, fields };

  return { ...form, steps };
};
